import api, { getApiBaseUrl } from './config';
import { ATTACHMENTS } from './endpoints';

// Dynamically import Resumable.js
let Resumable = null;

// Load Resumable.js from CDN
const loadResumableJS = () => {
  return new Promise((resolve, reject) => {
    if (window.Resumable) {
      resolve(window.Resumable);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/resumable.js/1.1.0/resumable.min.js';
    script.onload = () => {
      Resumable = window.Resumable;
      resolve(window.Resumable);
    };
    script.onerror = () => reject(new Error('Failed to load Resumable.js'));
    document.head.appendChild(script);
  });
};

export const documentsAPI = {
  // Initialize Resumable.js instance
  initializeResumable: async (options = {}) => {
    try {
      // Load Resumable.js if not already loaded
      const ResumableClass = await loadResumableJS();

      // Get auth token from localStorage
      const token = localStorage.getItem('token');

      const baseUrl = getApiBaseUrl();
      const target = baseUrl.startsWith('http')
        ? `${baseUrl.replace(/\/$/, '')}/attachments`
        : `${baseUrl.replace(/\/$/, '')}/attachments`;

      // Default configuration
      const defaultConfig = {
        target,
        chunkSize: 256 * 1024, // 256KB chunks (smaller for better reliability)
        simultaneousUploads: 1,
        testChunks: false,
        throttleProgressCallbacks: 1,
        method: 'multipart',
        uploadMethod: 'POST',
        maxFiles: 10,
        permanentErrors: [400, 401, 403, 404, 409, 415, 422, 500, 501], // Include 422 to stop retries on validation errors
        headers: {
          Accept: 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        query: {
          model_type: 'Dba',
        },
      };

      // Merge with custom options
      const config = { ...defaultConfig, ...options };

      // Create and return new Resumable instance
      const r = new ResumableClass(config);



      return r;
    } catch (error) {
      throw error;
    }
  },

  // Validate file before upload
  validateFile: (file) => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/zip',
      'application/x-zip-compressed',
      'text/plain',
      'text/csv'
    ];

    const errors = [];

    if (file.size > maxSize) {
      errors.push(`File size exceeds maximum allowed size of ${maxSize / (1024 * 1024)}MB`);
    }

    const fileType = file.file ? file.file.type : file.type;
    if (!allowedTypes.includes(fileType)) {
      errors.push(`File type "${fileType}" is not allowed`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Format file size helper
  formatFileSize: (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  },

  // Get file type category
  getFileTypeCategory: (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'spreadsheet';
    if (mimeType.includes('zip')) return 'archive';
    return 'other';
  },

  // Download attachment by ID
  downloadAttachment: async (attachmentId) => {
    try {
      const response = await api.get(ATTACHMENTS.DOWNLOAD(attachmentId), {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download file');
    }
  },

  // Delete attachment by ID
  deleteAttachment: async (attachmentId) => {
    try {
      const response = await api.delete(`/attachments/${attachmentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete file');
    }
  },

  // Get all attachments for a model
  getAttachments: async (modelType, modelId) => {
    try {
      const response = await api.get(ATTACHMENTS.LIST, {
        params: {
          model_type: modelType,
          model_id: modelId
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch attachments');
    }
  },

  downloadLeadFile: async (path) => {
    try {
      const response = await api.post(ATTACHMENTS.DOWNLOAD_BY_PATH, { path }, {
        responseType: 'blob', // Critical: get binary data
      });
      return response;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Download failed'
      );
    }
  }
};
